import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import CustomTable from 'src/ui-component/CustomTable/Index';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CustomSelect from 'src/ui-component/CustomSelect';
import CustomSearchBar from 'src/ui-component/CustomSearchBar';
import { columns, getData, UserManagementRow } from './UserManagementJSON';
import AddNewUser from './AddNewUser';
import LoadingOverlay from 'src/components/common/LoadingOverlay';

type UserGridRow = UserManagementRow;

const filterUserRows = (
  sourceRows: UserGridRow[],
  roleFilter: string,
  search: string
): UserGridRow[] => {
  let result = sourceRows;

  if (roleFilter !== 'All') {
    result = result.filter((row) => row.userRole === roleFilter);
  }

  const query = search.trim().toLowerCase();
  if (query.length > 0) {
    result = result.filter((row) =>
      Object.values(row).some(
        (value) =>
          typeof value === 'string' && value.toLowerCase().includes(query)
      )
    );
  }

  return result;
};

const UserManagement: React.FC = () => {
  const [pagination, setpagination] = useState(0);
  const [isSearch, setIsSearch] = useState(0);
  const [rows, setRows] = useState<UserGridRow[]>([]);
  const [filteredRows, setFilteredRows] = useState<UserGridRow[]>([]);
  const [customSelectValue, setcustomSelectValue] = useState('All');
  const [loading, setLoading] = useState(false);
  const [userAction, setUserAction] = useState<any>('');
  const [searchVal, setSearchVal] = useState('');

  //Status of the user
  const Status = (item: any) => (
    <Chip
      label={item?.Active ? 'Active' : 'Inactive'}
      color={item?.Active ? 'success' : 'error'}
      variant="outlined"
      sx={{ fontWeight: 'bold' }}
    />
  );

  //Subcodes display function
  const Subcodes = (item: any) => {
    const subcodes = item?.InspectorProfile?.SubCodes;

    // If no subcodes or not an inspector, show N/A
    if (!subcodes || !Array.isArray(subcodes) || subcodes.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          N/A
        </Typography>
      );
    }

    // If there are subcodes, display them as chips
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          wordWrap: 'break-word'
        }}
      >
        {subcodes.join(', ')}
      </Typography>
    );
  };

  const Action = (data: any) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <Tooltip title="Edit" arrow>
        <IconButton
          onClick={() => {
            setUserAction(data);
          }}
        >
          <EditTwoToneIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  //Method to get all users
  const handleApiCall = () => {
    setLoading(true);
    getData('/auth/getAllUsers', setRows, Status, Action, setLoading, Subcodes);
  };

  useEffect(() => {
    handleApiCall();
  }, []);

  // Role + search filters (preserved when returning from add/edit user)
  useEffect(() => {
    setFilteredRows(filterUserRows(rows, customSelectValue, searchVal));
  }, [rows, customSelectValue, searchVal]);

  return (
    <>
      {loading && <LoadingOverlay />}

      <Card>
        {userAction ? (
          <Box>
            <AddNewUser
              handleApiCall={handleApiCall}
              setUserAction={setUserAction}
              userAction={userAction}
            />
          </Box>
        ) : (
          <Box sx={{ m: 3 }}>
            <Grid container gap={2} justifyContent={'space-between'}>
              <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
                <Button
                  onClick={() => setUserAction('Add')}
                  variant="contained"
                  size="large"
                >
                  Add New User
                </Button>
              </Grid>

              <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
                <CustomSelect
                  setpagination={setpagination}
                  filterValue={customSelectValue}
                  setFilterValue={setcustomSelectValue}
                  title={'Filter by'}
                  options={[
                    { key: 'All', value: 'All' },
                    { key: 'Admin', value: 'Admin' },
                    { key: 'User', value: 'User' },
                    { key: 'Inspector', value: 'Inspector' }
                  ]}
                />
              </Grid>

              <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
                <CustomSearchBar
                  rows={rows}
                  setFilteredRows={setFilteredRows}
                  setpagination={setpagination}
                  setIsSearch={setIsSearch}
                  searchVal={searchVal}
                  setSearchVal={setSearchVal}
                  deferFilterToParent
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <CustomTable
                pagination={pagination}
                setSelectedRowData={() => { }}
                rows={filteredRows}
                columns={columns}
                isSearch={isSearch}
              />
            </Box>
          </Box>
        )}
      </Card>
    </>
  );
};

export default UserManagement;
